// src/components/exercise/SectionOverview.tsx
import React, { useState } from 'react';
import { useExerciseStore } from '../../stores/useExerciseStore';
import ProgressRing from './ProgressRing';
import type { SectionId } from '../../types/exercise';
import type { SectionProgress } from '../../types/progress';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore }   from '../../stores/useThemeStore';

interface SectionMeta { title: string; color: string; description: string; icon: React.ReactNode; }

const ICON = (paths: string) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={paths} />
  </svg>
);

const SECTION_META: Record<SectionId, SectionMeta> = {
  reading1:   { title: 'Reading I',       color: '#4f8cf7', description: 'True/false + comprehension',   icon: ICON('M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z') },
  vocabulary: { title: 'Vocabulary',      color: '#8b5cf6', description: 'Matching, fill-blank, MCQ',    icon: ICON('M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z') },
  grammar:    { title: 'Grammar',         color: '#f59e0b', description: 'Grammar in practice',          icon: ICON('M4 7V4h16v3M9 20h6M12 4v16') },
  video:      { title: 'Video Listening', color: '#ec4899', description: 'Listening comprehension',      icon: ICON('M15 10l4.553-2.277A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M3 8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z') },
  reading2:   { title: 'Reading II',      color: '#06b6d4', description: 'True/false + matching',        icon: ICON('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8') },
  audio:      { title: 'Audio Listening', color: '#34d399', description: 'Audio comprehension',          icon: ICON('M9 18V5l12-2v13M6 15.7A3 3 0 1 1 6 21a3 3 0 0 1 0-5.3M18 13.7A3 3 0 1 1 18 19a3 3 0 0 1 0-5.3') },
};

const SECTION_ORDER: SectionId[] = ['reading1', 'vocabulary', 'grammar', 'video', 'reading2', 'audio'];

type SectionStatus = 'not_started' | 'in_progress' | 'completed';

function getStatus(p: SectionProgress | undefined): SectionStatus {
  if (!p) return 'not_started';
  if (p.completed) return 'completed';
  return 'in_progress';
}

function getPct(p: SectionProgress | undefined, fallbackCount: number): number {
  if (!p) return 0;
  const max = p.maxScore > 0 ? p.maxScore : fallbackCount;
  if (!max) return 0;
  return Math.round((p.score / max) * 100);
}

const SectionOverview: React.FC = () => {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const currentBookId = useExerciseStore(s => s.currentBookId);
  const currentUnitId = useExerciseStore(s => s.currentUnitId);
  const exerciseData  = useExerciseStore(s => s.exerciseData);
  const progressData  = useExerciseStore(s => s.progressData);
  const startSection  = useExerciseStore(s => s.startSection);
  const resetSection  = useExerciseStore(s => s.resetSection);

  const scopedKey = currentBookId && currentUnitId ? `${currentBookId}_${currentUnitId}` : null;
  const unit      = currentUnitId ? exerciseData[currentUnitId] : null;
  const progress  = (scopedKey ? progressData[scopedKey] : null) ?? {};
  const sections = unit?.sections ?? [];

  let totalScore = 0, totalMax = 0;
  SECTION_ORDER.forEach(sid => {
    const p   = progress[sid] as SectionProgress | undefined;
    const sec = sections.find(s => s.id === sid);
    if (p && p.maxScore > 0) { totalScore += p.score; totalMax += p.maxScore; }
    else if (sec) { totalMax += sec.exercises.length; }
  });
  const overallPct   = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0;
  const overallColor = overallPct >= 90 ? C.success : overallPct >= 70 ? C.accent : C.premium;

  const [hoveredId,    setHoveredId]    = useState<SectionId | null>(null);
  const [resetConfirm, setResetConfirm] = useState<SectionId | null>(null);

  const handleReset = (sid: SectionId) => {
    if (resetConfirm === sid) {
      void resetSection(sid);
      setResetConfirm(null);
    } else {
      setResetConfirm(sid);
      setTimeout(() => setResetConfirm(prev => (prev === sid ? null : prev)), 3000);
    }
  };

  const ringBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding:      '24px 24px 20px',
        borderBottom: `1px solid ${C.border}`,
        background:   isDark ? 'rgba(13,18,32,0.6)' : 'rgba(248,250,252,0.9)',
        flexShrink:   0,
        display:      'flex',
        alignItems:   'center',
        gap:          '20px',
      }}>
        <ProgressRing
          percent={overallPct}
          size={56}
          stroke={4}
          color={overallColor}
          bgColor={ringBg}
          fontSize={11}
        />
        <div>
          <div style={{ fontSize: '1rem', fontWeight: 800, color: C.text, lineHeight: 1.2 }}>
            {(unit as any)?.title ?? 'Unit Overview'}
          </div>
          <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '4px' }}>
            {totalScore} / {totalMax} pts ·{' '}
            <span style={{ color: overallColor, fontWeight: 700 }}>{overallPct}%</span>
            {' '}complete
          </div>
        </div>
      </div>

      {/* Cards */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {SECTION_ORDER.map(sid => {
            const meta   = SECTION_META[sid];
            const sec    = sections.find(s => s.id === sid);
            const p      = progress[sid] as SectionProgress | undefined;
            const status = getStatus(p);
            const pct    = getPct(p, sec?.exercises.length ?? 0);
            const isHov  = hoveredId === sid;

            if (!sec) return null;

            const startLabel = status === 'not_started' ? 'Start' : status === 'completed' ? 'Redo' : 'Continue';

            return (
              <div
                key={sid}
                onMouseEnter={() => setHoveredId(sid)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  background:   isHov
                    ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)')
                    : C.card,
                  border:       `1px solid ${isHov ? C.borderH : C.border}`,
                  borderRadius: '14px',
                  padding:      '14px 16px',
                  transition:   'all 0.2s ease',
                  transform:    isHov ? 'translateX(2px)' : 'none',
                  boxShadow:    isHov ? '0 4px 16px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flexShrink: 0 }}>
                    <ProgressRing
                      percent={pct}
                      size={44}
                      stroke={3}
                      color={meta.color}
                      bgColor={ringBg}
                      fontSize={9}
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ color: meta.color, display: 'flex', alignItems: 'center' }}>
                        {meta.icon}
                      </span>
                      <span style={{ fontSize: '0.88rem', fontWeight: 700, color: C.text }}>{meta.title}</span>
                      {status === 'completed' && (
                        <span style={{
                          fontSize: '0.62rem', fontWeight: 700, color: C.success,
                          background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)',
                          borderRadius: '999px', padding: '1px 7px', letterSpacing: '0.07em', textTransform: 'uppercase',
                        }}>Done</span>
                      )}
                      {status === 'in_progress' && (
                        <span style={{
                          fontSize: '0.62rem', fontWeight: 700, color: C.premium,
                          background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)',
                          borderRadius: '999px', padding: '1px 7px', letterSpacing: '0.07em', textTransform: 'uppercase',
                        }}>In Progress</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: C.muted, marginTop: '3px', lineHeight: 1.4 }}>
                      {meta.description} · {sec.exercises.length} exercises
                      {p && p.maxScore > 0 && (
                        <span style={{ color: status === 'completed' ? C.success : C.muted }}>
                          {' · '}{p.score}/{p.maxScore} pts
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
                    {status !== 'not_started' && (
                      <button
                        onClick={() => handleReset(sid)}
                        title={resetConfirm === sid ? 'Click again to confirm' : 'Reset progress'}
                        style={{
                          padding:      '6px 10px',
                          borderRadius: '8px',
                          border:       `1px solid ${resetConfirm === sid ? 'rgba(248,113,113,0.3)' : C.border}`,
                          background:   resetConfirm === sid ? 'rgba(248,113,113,0.08)' : 'transparent',
                          color:        resetConfirm === sid ? C.error : C.muted,
                          fontSize:     '0.72rem',
                          fontWeight:   700,
                          cursor:       'pointer',
                          transition:   'all 0.15s ease',
                          whiteSpace:   'nowrap',
                          fontFamily:   'inherit',
                        }}
                      >
                        {resetConfirm === sid ? 'Sure?' : '↺'}
                      </button>
                    )}

                    <button
                      onClick={() => void startSection(sid)}
                      style={{
                        padding:       '7px 16px',
                        borderRadius:  '999px',
                        border:        'none',
                        background:    status === 'completed'
                          ? 'rgba(52,211,153,0.12)'
                          : `linear-gradient(135deg, ${meta.color} 0%, ${meta.color}cc 100%)`,
                        color:         status === 'completed' ? C.success : '#fff',
                        fontSize:      '0.72rem',
                        fontWeight:    700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        cursor:        'pointer',
                        transition:    'all 0.18s ease',
                        whiteSpace:    'nowrap',
                        fontFamily:    'inherit',
                        boxShadow:     status !== 'completed' ? `0 4px 12px ${meta.color}40` : 'none',
                      }}
                    >
                      {startLabel}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ height: '12px' }} />
      </div>
    </div>
  );
};

export default SectionOverview;
